import type { Locale } from '@xdocs/i18n';

/**
 * STOP-GAP local catalog for the `shares:` scope (F63/F64/F72), following
 * the `search:`/`sharing:`/`discover:` precedent — see
 * `apps/web/src/features/search/search.i18n.ts` for the pattern. NOT a
 * second i18n runtime (E18/§13.3 ban); `packages/i18n/**` is outside this
 * task's ownership.
 *
 * OPEN ISSUE: fold into `packages/i18n/src/catalogs/{en,ro}/shares.json`.
 */
const en = {
  'page.title': 'My shares',
  'page.description': 'Everything you’ve shared — manage visibility, links, and revoke access.',
  'tabs.shares': 'My shares',
  'tabs.referrals': 'Referrals',
  'list.column.document': 'Document',
  'list.column.visibility': 'Visibility',
  'list.column.moderation': 'Status',
  'list.column.comments': 'Comments',
  'list.column.likes': 'Likes',
  'list.column.updatedAt': 'Updated',
  'list.action.manage': 'Manage',
  'list.action.revoke': 'Revoke',
  'list.empty.title': 'You haven’t shared anything yet',
  'list.empty.description': 'Share a document from its page to see it here.',
  'list.error.title': "Couldn't load your shares.",
  'list.noResults.title': 'No shares match “{query}”',
  'list.searchLabel': 'Search your shares',
  'list.searchPlaceholder': 'Search by document title…',
  'visibility.private': 'Private',
  'visibility.public': 'Public',
  'visibility.link': 'Link',
  'visibility.user': 'Shared',
  'moderation.pending': 'Pending',
  'moderation.approved': 'Approved',
  'moderation.rejected': 'Rejected',
  'toast.revoked.action': 'Made private',
  'toast.revokeFailed': "Couldn't update this share — try again.",
  'referral.column.referred': 'Referred',
  'referral.column.document': 'Document',
  'referral.column.status': 'Status',
  'referral.column.earned': 'Earned',
  'referral.status.pending': 'Pending',
  'referral.status.converted': 'Converted',
  'referral.status.credited': 'Credited',
  'referral.status.void': 'Void',
  'referral.empty.title': 'No referrals yet',
  'referral.empty.description': 'Share a document with your referral link and earn credits when someone signs up through it.',
  'referral.error.title': "Couldn't load your referrals.",
  'referral.earned': '{count, plural, one {# credit} other {# credits}}',
  'link.page.title': 'Shared with you',
  'link.notFound.title': 'This link doesn’t work',
  'link.notFound.description': 'It may have expired, been revoked, or never existed.',
  'link.notFound.cta': 'Go to xDocs',
} as const;

const ro: Record<keyof typeof en, string> = {
  'page.title': 'Distribuirile mele',
  'page.description': 'Tot ce ai distribuit — gestionează vizibilitatea, linkurile și revocă accesul.',
  'tabs.shares': 'Distribuirile mele',
  'tabs.referrals': 'Recomandări',
  'list.column.document': 'Document',
  'list.column.visibility': 'Vizibilitate',
  'list.column.moderation': 'Stare',
  'list.column.comments': 'Comentarii',
  'list.column.likes': 'Aprecieri',
  'list.column.updatedAt': 'Actualizat',
  'list.action.manage': 'Gestionează',
  'list.action.revoke': 'Revocă',
  'list.empty.title': 'Nu ai distribuit nimic încă',
  'list.empty.description': 'Distribuie un document din pagina lui ca să apară aici.',
  'list.error.title': 'Nu am putut încărca distribuirile tale.',
  'list.noResults.title': 'Nicio distribuire nu se potrivește cu „{query}”',
  'list.searchLabel': 'Caută în distribuirile tale',
  'list.searchPlaceholder': 'Caută după titlul documentului…',
  'visibility.private': 'Privat',
  'visibility.public': 'Public',
  'visibility.link': 'Link',
  'visibility.user': 'Distribuit',
  'moderation.pending': 'În așteptare',
  'moderation.approved': 'Aprobat',
  'moderation.rejected': 'Respins',
  'toast.revoked.action': 'Făcut privat',
  'toast.revokeFailed': 'Nu am putut actualiza această distribuire — încearcă din nou.',
  'referral.column.referred': 'Recomandat',
  'referral.column.document': 'Document',
  'referral.column.status': 'Stare',
  'referral.column.earned': 'Câștigat',
  'referral.status.pending': 'În așteptare',
  'referral.status.converted': 'Convertit',
  'referral.status.credited': 'Creditat',
  'referral.status.void': 'Anulat',
  'referral.empty.title': 'Nicio recomandare încă',
  'referral.empty.description': 'Distribuie un document cu linkul tău de recomandare și câștigă credite când cineva se înscrie prin el.',
  'referral.error.title': 'Nu am putut încărca recomandările tale.',
  'referral.earned': '{count, plural, one {# credit} few {# credite} other {# de credite}}',
  'link.page.title': 'Distribuit cu tine',
  'link.notFound.title': 'Acest link nu funcționează',
  'link.notFound.description': 'Poate a expirat, a fost revocat, sau nu a existat niciodată.',
  'link.notFound.cta': 'Mergi la xDocs',
};

export const SHARES_CATALOG: Record<Locale, Record<keyof typeof en, string>> = { en, ro };

export type SharesCatalogKey = keyof typeof en;
